import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layers, Check } from "lucide-react";
import { api } from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import { IconArrowUpRight } from "@/lib/icons";
import { visibleGroups, flatVisibleSteps } from "@/lib/practiceSetupConfig";
import PracticeSetupPanel from "@/components/setup/PracticeSetupPanel"; // eslint-disable-line no-unused-vars
import { useProfile, useClients, useProviders } from "./psPrimitives";
import { AccountScreen, ClientsScreen, EhrScreen, PayersFullOnly } from "./screens";
import { ProvidersScreen } from "./ProvidersScreen";
import { CodingRulesScreen } from "./CodingRulesScreen";
import { NoteTemplateScreen } from "./NoteTemplateScreen";
import { VisitTypesScreen } from "./VisitTypesScreen";
import { TovListScreen } from "./TovListScreen";
import { FullPane } from "./fullPanes";
import { AvailabilityScreen } from "./AvailabilityScreen";
import { IntakeScreen } from "./IntakeScreen";
import { setPsClient, usePsClient, useMaster, MasterStep, LocationsMaster, PayersMaster, ReferringMaster, RenderingMaster } from "./psMasters";
import { CodesMaster } from "./psCodes";
import { BillingRenderingScreen } from "./BillingRenderingScreen";

export default function PracticeSetupV2() {
  // Always default to the API-supported state on load. The Full state is a
  // review-only mode reached via the dev switch; it is not persisted.
  const [mode, setMode] = useState("api");
  const deep = useMemo(() => new URLSearchParams(window.location.search), []);
  const [active, setActive] = useState(() => deep.get("step") || "profile");
  const deepNpi = active === "availability" ? deep.get("npi") : null;
  useEffect(() => { const c = deep.get("client"); if (c) setPsClient(c); }, [deep]);

  // Review-only switch: dev builds, or force-on with localStorage.revq.psModeSwitch="1".
  const DEV_SWITCH = process.env.NODE_ENV !== "production" || localStorage.getItem("revq.psModeSwitch") === "1";
  const effectiveMode = DEV_SWITCH ? mode : "api";
  const setModePersist = (m) => setMode(m);
  const onOpenDq = undefined;

  const { data: profile } = useProfile();
  const { data: clients = [] } = useClients();
  const { data: providers = [] } = useProviders();
  const { cid } = usePsClient();
  const { data: payers = [] } = useMaster("payers", cid);
  const { data: locs = [] } = useMaster("service_locations", cid);
  const { data: rends = [] } = useMaster("rendering_providers", cid);
  const { data: links } = useQuery({
    queryKey: ["links", cid], enabled: !!cid, staleTime: 10_000,
    queryFn: async () => (await api.get(`/api/edi/data-migration/clients/${encodeURIComponent(cid)}/links?min_claims=1&active_days=0`)).data,
  });

  const done = useMemo(() => ({
    profile: !!profile?.id,
    clients: clients.length > 0,
    providers: providers.some((p) => String(p.client_id || p.client_4digit_id || "") === String(cid)),
    ehr: clients.some((c) => c.roster_status === "ready"),
    payers: payers.length > 0,
    locations: locs.length > 0,
    rendering: rends.length > 0,
    billrender: (links?.billing_providers || []).some((b) => (b.renderings || b.rendering_providers || b.links || b.relationships || []).length > 0),
  }), [profile, clients, providers, payers, locs, rends, links, cid]);

  const groups = visibleGroups(effectiveMode);
  const steps = flatVisibleSteps(effectiveMode);
  const required = steps.filter((s) => s.live && !s.optional);
  const doneCount = required.filter((s) => done[s.id]).length;

  const Body = renderStep(active, effectiveMode, onOpenDq, setActive, deepNpi);

  return (
    <div className="fade-up relative">
      {/* header */}
      <TitleBlock
        testid="ps-header"
        title="Practice Setup"
        subtitle="Your account, providers, payers and billing setup. Everything autosaves."
        chips={clients.filter((c) => String(c.client_id) === String(cid)).map((c) => (
          <span key={c.client_id} data-testid={`ps-client-chip-${c.client_id}`} title={c.name || undefined}
            className="text-[11px] font-semibold px-2.5 py-[3px] rounded-full border border-[#E9E9EC] bg-white text-[var(--ink2)] tabular-nums">
            Client {c.client_id}
          </span>
        ))}
        actions={<>
          <div className="text-[13px] text-[var(--gray2)]">
            <b className="font-bold text-[var(--ink)]">{doneCount} of {required.length}</b> steps set up
          </div>
          <a href="/practice-setup-v1" target="_blank" rel="noopener noreferrer" data-testid="ps-v1-btn"
            title="Open the previous version (v1) for reference"
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2.5 py-1 rounded-full border border-[#E9E9EC] text-[var(--gray2)] hover:text-[var(--ink)] hover:border-[var(--gray3)] transition">
            v1 <IconArrowUpRight size={12} stroke={2} aria-hidden />
          </a>
        </>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6 items-start">
        {/* nav */}
        <nav className="lg:sticky lg:top-4 self-start" data-testid="ps-nav">
          {/* mobile: horizontal step scroller (<lg) */}
          <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 mb-1 -mx-1 px-1 ps-scroll-x" data-testid="ps-nav-mobile">
            {steps.map((s) => {
              const on = active === s.id;
              return (
                <button key={s.id} onClick={() => setActive(s.id)} data-testid={`ps-step-m-${s.id}`}
                  className={`flex-none inline-flex items-center gap-2 px-3 py-2 rounded-full border text-[12.5px] whitespace-nowrap transition ${on ? "bg-[var(--ink)] text-white border-transparent" : "bg-white border-[#E9E9EC] text-[var(--ink2)]"}`}>
                  <StepDot isDone={done[s.id]} on={on} optional={s.optional} />
                  {s.name}
                </button>
              );
            })}
          </div>

          {/* desktop: group-card rail (lg+) */}
          <div className="hidden lg:flex lg:flex-col gap-3.5">
            {groups.map((g, gi) => {
              const liveSteps = g.steps.filter((s) => s.live);
              const allOptional = liveSteps.length > 0 && liveSteps.every((s) => s.optional);
              const counted = liveSteps.filter((s) => !s.optional);
              const total = counted.length;
              const doneN = counted.filter((s) => done[s.id]).length;
              const countLabel = total === 0 ? "" : allOptional ? "Optional" : `${doneN} / ${total}`;
              return (
                <div key={g.id} className="rounded-[16px] border border-[#E9E9EC] bg-white overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[#E9E9EC]">
                    <span className="w-6 h-6 rounded-[7px] bg-[#F1F1F4] grid place-items-center text-[12px] font-bold text-[var(--gray2)] flex-none">{gi + 1}</span>
                    <span className="flex-1 min-w-0 font-bold text-[13px] text-[var(--ink2)]">{g.name}</span>
                    {countLabel ? <span className="text-[12px] text-[var(--gray3)] tabular-nums flex-none">{countLabel}</span> : null}
                  </div>
                  {g.steps.map((s) => {
                    const on = active === s.id;
                    return (
                      <button key={s.id} onClick={() => setActive(s.id)} data-testid={`ps-step-${s.id}`}
                        className={`w-full text-left flex items-center gap-3 px-4 py-[11px] border-t border-[#E9E9EC] text-[13px] transition ${on ? "bg-[var(--ink)] text-white" : "text-[var(--ink2)] hover:bg-[rgba(88,86,214,.06)]"}`}>
                        <StepDot isDone={done[s.id]} on={on} optional={s.optional} />
                        <span className="flex-1 min-w-0 truncate">{s.name}</span>
                        {s.optional ? <span className={`text-[12px] flex-none ${on ? "text-white/70" : "text-[var(--gray3)]"}`}>Optional</span> : null}
                        {!s.live ? <span className="w-1.5 h-1.5 rounded-full flex-none" style={{ background: "#5856D6" }} /> : null}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </nav>

        {/* pane */}
        <div className="min-w-0 rounded-[20px] border border-[#E9E9EC] bg-white px-5 sm:px-9 pt-7 sm:pt-8 pb-8 sm:pb-9 min-h-[560px]" data-testid="ps-pane">{Body}</div>
      </div>

      {/* floating API / Full switch — review tool, dev-gated (hidden from customers) */}
      {DEV_SWITCH ? (
        <div data-testid="ps-mode-switch" className="fixed bottom-5 right-5 z-[8000] flex items-center gap-2 rounded-full bg-white border border-black/[.1] px-1.5 py-1.5" style={{ boxShadow: "0 10px 30px rgba(0,0,0,.15)" }}>
        <Layers size={13} className="text-[var(--gray2)] ml-1.5" />
        {["api", "full"].map((m) => (
          <button key={m} onClick={() => setModePersist(m)} data-testid={`ps-mode-${m}`}
            className={`text-[11px] font-semibold px-3 py-1.5 rounded-full transition ${mode === m ? "bg-[var(--ink)] text-white" : "text-[var(--ink2)] hover:bg-black/[.05]"}`}>
            {m === "api" ? "API supported" : "Full"}
          </button>
        ))}
        </div>
      ) : null}

    </div>
  );
}

function StepDot({ isDone, on, optional }) {
  if (isDone) {
    return (
      <span className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ${on ? "bg-white" : "bg-[var(--ink)]"}`}>
        <Check size={11} strokeWidth={3} className={on ? "text-[var(--ink)]" : "text-white"} />
      </span>
    );
  }
  return <span className={`w-5 h-5 rounded-full flex-shrink-0 border-[1.5px] ${optional ? "border-dashed " : ""}${on ? "border-white/55" : "border-[var(--gray3)]"}`} />;
}

function renderStep(id, mode, onOpenDq, onGoStep, deepNpi) {
  switch (id) {
    case "profile": return <AccountScreen mode={mode} onOpenDq={onOpenDq} />;
    case "clients": return <ClientsScreen mode={mode} onOpenDq={onOpenDq} />;
    case "providers": return <ProvidersScreen mode={mode} onOpenDq={onOpenDq} />;
    case "ehr": return <EhrScreen mode={mode} onOpenDq={onOpenDq} />;
    case "payers": return <><MasterStep eyebrow="Connections & Enrollment" title="Payer list" lede="The payers this client bills. revQ found most of them in your migrated claims.">{(cid) => <PayersMaster cid={cid} />}</MasterStep>{mode === "full" ? <PayersFullOnly /> : null}</>;
    case "rendering": return <MasterStep eyebrow="Your Account" title="Rendering providers" lede="The providers who deliver the care (NM1*82). This list fills the rendering provider on the claim form and the Billing relationships below. Linking an NPI to a client in Providers · NPI adds it here automatically.">{(cid) => <RenderingMaster cid={cid} />}</MasterStep>;
    case "billrender": return <BillingRenderingScreen />;
    case "availability": return <AvailabilityScreen mode={mode} initialNpi={deepNpi} onGoProviders={() => onGoStep("providers")} />;
    case "intake": return <IntakeScreen mode={mode} onGoProviders={() => onGoStep("providers")} />;
    case "referring": return <MasterStep eyebrow="Your Account · Optional" title="Referring physicians" lede="Physicians who refer patients to this practice. They appear in the referring-provider picker on claims.">{(cid) => <ReferringMaster cid={cid} />}</MasterStep>;
    case "locations": return <><MasterStep eyebrow="Your Account" title={mode === "full" ? "Locations & place of service" : "Service locations"} lede="Where care is delivered. These are the service locations on your claims.">{(cid) => <LocationsMaster cid={cid} />}</MasterStep>{mode === "full" ? <div className="mt-8"><FullOnlyScreen id={id} /></div> : null}</>;
    case "codes": return <MasterStep eyebrow="Billing Rules · Optional" title="Codes & modifiers" lede="The codes and modifiers revQ offers for this client in claim pickers.">{(cid) => <CodesMaster cid={cid} />}</MasterStep>;
    case "notetpl": return <NoteTemplateScreen mode={mode} onOpenDq={onOpenDq} onGoStep={onGoStep} />;
    case "visittypes": return mode === "full" ? <VisitTypesScreen onOpenDq={onOpenDq} /> : <TovListScreen />;
    case "coding": return <CodingRulesScreen mode={mode} onOpenDq={onOpenDq} />;
    default: return <FullOnlyScreen id={id} onOpenDq={onOpenDq} />;
  }
}

function FullOnlyScreen({ id, onOpenDq }) {
  const { data: profile } = useProfile();
  const step = flatVisibleSteps("full").find((s) => s.id === id);
  if (!step) return null;
  return <FullPane step={step} profile={profile} onOpenDq={onOpenDq} />;
}
